import { AlertCircle, Home, RefreshCw } from 'lucide-react';

import React, { Component, ErrorInfo, ReactNode } from 'react';

import { Button } from '@splitfiesta/ui';

import type { ApplicationError, ErrorContext } from '@/lib/errors';
import {
  ErrorFactory,
  addBreadcrumb,
  isApplicationError,
  logError,
} from '@/lib/errors';

import { useAuthStore } from '@/stores/authStore';

interface Props {
  children: ReactNode;
  fallback?: (
    error: Error,
    errorInfo: ErrorInfo,
    reset: () => void
  ) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Name of the error boundary for tracking
   */
  boundaryName?: string;
}

interface State {
  hasError: boolean;
  error: Error | ApplicationError | null;
  errorInfo: ErrorInfo | null;
  recoveryAttempts: number;
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * Enhanced with:
 * - Type-safe error handling using ApplicationError types
 * - Automatic logging with PII sanitization via SecureErrorLogger
 * - Recovery attempt tracking
 * - Rich error context capture
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      recoveryAttempts: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Normalize error to ApplicationError
    const normalizedError = isApplicationError(error)
      ? error
      : ErrorFactory.normalize(error);

    // Build error context
    const context = this.buildErrorContext(errorInfo);

    // Log error with SecureErrorLogger (handles PII sanitization)
    logError(normalizedError, context);

    // Add breadcrumb for this error boundary catch
    addBreadcrumb({
      category: 'ui',
      message: `Error caught by ${this.props.boundaryName || 'ErrorBoundary'}`,
      level: 'error',
      data: {
        errorMessage: error.message,
        componentStack: errorInfo.componentStack,
      },
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error details
    this.setState(prevState => ({
      error: normalizedError,
      errorInfo,
      recoveryAttempts: prevState.recoveryAttempts + 1,
    }));
  }

  /**
   * Build comprehensive error context
   */
  private buildErrorContext(errorInfo: ErrorInfo): Partial<ErrorContext> {
    const authState = useAuthStore.getState();

    return {
      user: authState.user
        ? {
            id: authState.user.id,
            email: authState.user.email,
          }
        : undefined,
      isAuthenticated: authState.isAuthenticated,
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      search: typeof window !== 'undefined' ? window.location.search : '',
      componentStack: errorInfo.componentStack ?? undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: new Date(),
      storeSnapshot: {
        auth: {
          isAuthenticated: authState.isAuthenticated,
          userId: authState.user?.id,
        },
      },
    };
  }

  reset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      recoveryAttempts: 0,
    });

    // Add breadcrumb for recovery attempt
    addBreadcrumb({
      category: 'user_action',
      message: 'User attempted error recovery',
      level: 'info',
      data: {
        boundaryName: this.props.boundaryName,
      },
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Allow custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo!,
          this.reset
        );
      }

      // Default fallback UI
      return (
        <DefaultErrorFallback error={this.state.error} reset={this.reset} />
      );
    }

    return this.props.children;
  }
}

/**
 * Default error fallback UI component
 */
interface DefaultErrorFallbackProps {
  error: Error;
  reset: () => void;
}

function DefaultErrorFallback({ error, reset }: DefaultErrorFallbackProps) {
  const isDevelopment = import.meta.env.DEV;

  return (
    <div className="min-h-screen bg-canvas dark:bg-canvas-dark flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sm:p-8">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Error Title */}
          <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-gray-100 mb-2">
            Oops! Something went wrong
          </h1>

          {/* Error Description */}
          <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
            We encountered an unexpected error. Don&apos;t worry, your data is
            safe. You can try refreshing the page or going back to the home
            page.
          </p>

          {/* Error Details (Development Only) */}
          {isDevelopment && error && (
            <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <p className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                {error.message}
              </p>
              {error.stack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                    View Stack Trace
                  </summary>
                  <pre className="mt-2 text-xs font-mono text-gray-600 dark:text-gray-400 overflow-x-auto">
                    {error.stack}
                  </pre>
                </details>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="primary"
              className="flex-1"
              onClick={reset}
              leftIcon={<RefreshCw className="w-4 h-4" />}
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => (window.location.href = '/')}
              leftIcon={<Home className="w-4 h-4" />}
            >
              Go Home
            </Button>
          </div>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          If this problem persists, please contact support or try again later.
        </p>
      </div>
    </div>
  );
}

export default ErrorBoundary;
